import { useState, useRef } from 'react';
import { Upload, X, AlertTriangle, Loader2, Check, BookOpen, Download, ImageIcon, ScanLine } from 'lucide-react';
import { extractPdfCover } from '@/lib/pdfCover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useAppContext } from '@/hooks/useAppContext';
import { getEffectiveBlossomServers } from '@/lib/appBlossom';
import { uploadWithProgress, uploadFileToAllServers, formatBytes } from '@/lib/blossomUpload';
import { KIND_MAGAZINE_ISSUE, KIND_MAGAZINE } from '@/lib/pressstrKinds';
import type { NostrEvent } from '@nostrify/nostrify';

interface UploadIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  magazine?: NostrEvent;
  onSuccess?: () => void;
}

interface UploadProgress {
  stage: 'idle' | 'uploading-pdf' | 'uploading-cover' | 'publishing' | 'complete' | 'error';
  message: string;
  progress: number;
  fileLoaded?: number;
  fileTotal?: number;
  error?: string;
}

function slugify(text: string): string {
  return text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
}

export function UploadIssueDialog({ open, onOpenChange, magazine, onSuccess }: UploadIssueDialogProps) {
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const { mutateAsync: publishEvent } = useNostrPublish();

  const [title, setTitle] = useState('');
  const [issueId, setIssueId] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManual, setSlugManual] = useState(false);
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverAutoExtracted, setCoverAutoExtracted] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [publishedAt, setPublishedAt] = useState('');
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({ stage: 'idle', message: '', progress: 0 });

  const pdfInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugManual) setSlug(slugify(value));
  };

  const handlePdfSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast({ title: 'Invalid file', description: 'Please select a PDF file.', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    const buf = await file.arrayBuffer();
    const clonedPdf = new File([buf], file.name, { type: file.type });
    setPdfFile(clonedPdf);
    e.target.value = '';

    // Auto-extract cover from first page
    await doExtractCover(clonedPdf);
  };

  const doExtractCover = async (pdfSource?: File) => {
    const src = pdfSource ?? pdfFile;
    if (!src) {
      toast({ title: 'No PDF selected', description: 'Select a PDF first.', variant: 'destructive' });
      return;
    }
    setIsExtracting(true);
    try {
      const cover = await extractPdfCover(src);
      setCoverFile(cover);
      setCoverPreview(URL.createObjectURL(cover));
      setCoverAutoExtracted(true);
    } catch (err) {
      toast({
        title: 'Cover extraction failed',
        description: err instanceof Error ? err.message : 'Could not render PDF page 1.',
        variant: 'destructive',
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleCoverSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please select an image file.', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    const buf = await file.arrayBuffer();
    const cloned = new File([buf], file.name, { type: file.type });
    setCoverFile(cloned);
    setCoverPreview(URL.createObjectURL(cloned));
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!pdfFile || !title.trim() || !issueId.trim()) {
      toast({ title: 'Missing fields', description: 'Title, issue number, and PDF file are required.', variant: 'destructive' });
      return;
    }
    if (!user) {
      toast({ title: 'Login required', description: 'You must be logged in to upload issues.', variant: 'destructive' });
      return;
    }

    const servers = getEffectiveBlossomServers(config.blossomServerMetadata, config.useAppBlossomServers);
    if (servers.length === 0) {
      toast({ title: 'No Blossom servers', description: 'Configure Blossom servers in settings.', variant: 'destructive' });
      return;
    }

    setUploadProgress({ stage: 'uploading-pdf', message: 'Uploading PDF to Blossom...', progress: 10, fileLoaded: 0, fileTotal: pdfFile.size });

    try {
      // Upload PDF with XHR progress
      const pdfResult = await uploadWithProgress(
        pdfFile,
        servers,
        user.signer,
        ({ loaded, total }) => {
          setUploadProgress(prev => ({ ...prev, fileLoaded: loaded, fileTotal: total }));
        },
      );

      // Upload cover image if provided
      let coverUrl: string | undefined;
      if (coverFile) {
        setUploadProgress({ stage: 'uploading-cover', message: 'Uploading cover image...', progress: 75 });
        const coverResult = await uploadFileToAllServers(coverFile, servers, user.signer);
        coverUrl = coverResult.url;
      }

      // Publish Nostr event
      setUploadProgress({ stage: 'publishing', message: 'Publishing to Nostr...', progress: 90 });

      const dTag = slug.trim() || slugify(title.trim());
      const eventTags: string[][] = [
        ['d', dTag],
        ['title', title.trim()],
        ['issue', issueId.trim()],
        ['url', pdfResult.url],
        ['m', 'application/pdf'],
        ['size', String(pdfFile.size)],
        ['x', pdfResult.sha256],
        ['alt', `Magazine Issue: ${title.trim()}, Issue ${issueId.trim()}`],
      ];

      if (magazine) {
        const magD = magazine.tags.find(([n]) => n === 'd')?.[1] ?? '';
        eventTags.push(['a', `${KIND_MAGAZINE}:${magazine.pubkey}:${magD}`]);
      }

      if (coverUrl) eventTags.push(['image', coverUrl]);
      if (summary.trim()) eventTags.push(['summary', summary.trim()]);
      if (publishedAt) {
        const ts = Math.floor(new Date(publishedAt).getTime() / 1000);
        if (!isNaN(ts)) eventTags.push(['published_at', String(ts)]);
      }

      await publishEvent({
        kind: KIND_MAGAZINE_ISSUE,
        content: content.trim(),
        tags: eventTags,
      });

      setUploadProgress({ stage: 'complete', message: 'Issue uploaded!', progress: 100 });
      onSuccess?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setUploadProgress({ stage: 'error', message: 'Upload failed', progress: 0, error: msg });
      toast({ title: 'Upload failed', description: msg, variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setTitle('');
    setIssueId('');
    setSlug('');
    setSlugManual(false);
    setSummary('');
    setContent('');
    setPdfFile(null);
    setCoverFile(null);
    setCoverPreview(null);
    setCoverAutoExtracted(false);
    setIsExtracting(false);
    setPublishedAt('');
    setUploadProgress({ stage: 'idle', message: '', progress: 0 });
  };

  const handleClose = () => {
    if (uploadProgress.stage === 'uploading-pdf' || uploadProgress.stage === 'uploading-cover' || uploadProgress.stage === 'publishing') return;
    resetForm();
    onOpenChange(false);
  };

  // Progress view
  if (uploadProgress.stage !== 'idle') {
    return (
      <Dialog open={open} onOpenChange={uploadProgress.stage === 'complete' || uploadProgress.stage === 'error' ? handleClose : undefined}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              {uploadProgress.stage === 'complete' ? 'Issue Published!' : uploadProgress.stage === 'error' ? 'Upload Failed' : 'Uploading Issue...'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Progress bars */}
            {uploadProgress.stage !== 'complete' && uploadProgress.stage !== 'error' && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{uploadProgress.message}</span>
                    <span className="font-medium">{uploadProgress.progress}%</span>
                  </div>
                  <Progress value={uploadProgress.progress} />
                </div>

                {/* Byte-level progress for PDF upload */}
                {uploadProgress.fileTotal !== undefined && uploadProgress.fileTotal > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Upload className="w-3 h-3" />
                        File upload
                      </span>
                      <span className="tabular-nums">
                        {formatBytes(uploadProgress.fileLoaded ?? 0)} / {formatBytes(uploadProgress.fileTotal)}
                        {' '}
                        <span className="text-foreground font-medium">
                          ({Math.round(((uploadProgress.fileLoaded ?? 0) / uploadProgress.fileTotal) * 100)}%)
                        </span>
                      </span>
                    </div>
                    <Progress
                      value={Math.round(((uploadProgress.fileLoaded ?? 0) / uploadProgress.fileTotal) * 100)}
                      className="h-2"
                    />
                  </div>
                )}
              </div>
            )}

            {uploadProgress.stage === 'error' && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{uploadProgress.error}</AlertDescription>
              </Alert>
            )}

            {uploadProgress.stage === 'complete' && (
              <Alert className="border-green-500 bg-green-500/10">
                <Check className="h-4 w-4 text-green-500" />
                <AlertDescription className="text-green-700 dark:text-green-400">
                  The issue has been published to Nostr!
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3">
              {uploadProgress.stage === 'complete' && (
                <Button className="flex-1" onClick={handleClose}>Close</Button>
              )}
              {uploadProgress.stage === 'error' && (
                <>
                  <Button variant="outline" className="flex-1" onClick={() => setUploadProgress({ stage: 'idle', message: '', progress: 0 })}>
                    Try Again
                  </Button>
                  <Button variant="outline" onClick={handleClose}>Cancel</Button>
                </>
              )}
              {uploadProgress.stage !== 'complete' && uploadProgress.stage !== 'error' && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground w-full justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Please wait, do not close this window...
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Upload Magazine Issue
          </DialogTitle>
          <DialogDescription>
            {magazine
              ? `Upload a new issue for "${magazine.tags.find(([n]) => n === 'title')?.[1] ?? 'this magazine'}".`
              : 'Upload a new magazine issue to Nostr via Blossom.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* PDF Upload */}
          <div>
            <Label className="text-sm font-medium">PDF File *</Label>
            <input ref={pdfInputRef} type="file" accept="application/pdf" onChange={handlePdfSelect} className="hidden" />
            {pdfFile ? (
              <div className="mt-2 flex items-center gap-3 p-3 rounded-lg border bg-muted/50">
                <Download className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{pdfFile.name}</p>
                  <p className="text-xs text-muted-foreground">{formatBytes(pdfFile.size)}</p>
                </div>
                <button onClick={() => setPdfFile(null)} className="p-1 hover:text-destructive">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => pdfInputRef.current?.click()}
                className="mt-2 w-full border-2 border-dashed rounded-xl p-8 text-center hover:border-primary hover:bg-muted/50 transition-colors"
              >
                <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">Click to select a PDF file</p>
              </button>
            )}
          </div>

          {/* Cover Image */}
          <div>
            <Label className="text-sm font-medium">Cover Image</Label>
            <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverSelect} className="hidden" />
            <div className="mt-2 flex items-start gap-4">
              {coverPreview ? (
                <div className="relative w-20 aspect-[2/3] rounded-lg overflow-hidden bg-muted shrink-0">
                  <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                  <button
                    onClick={() => { setCoverFile(null); setCoverPreview(null); setCoverAutoExtracted(false); }}
                    className="absolute top-1 right-1 p-0.5 rounded-full bg-black/60 text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => coverInputRef.current?.click()}
                  className="w-20 aspect-[2/3] border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-1 hover:border-primary hover:bg-muted/50 transition-colors shrink-0"
                >
                  <Upload className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
              <div className="flex-1 pt-1 space-y-2">
                {coverAutoExtracted && (
                  <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    Extracted from PDF page 1
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Cover image (portrait 2:3 ratio).
                </p>
                <div className="flex flex-wrap gap-2">
                  {pdfFile && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      disabled={isExtracting}
                      onClick={() => doExtractCover()}
                    >
                      {isExtracting
                        ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Extracting…</>
                        : <><ScanLine className="w-3 h-3 mr-1" />Extract from PDF</>
                      }
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => coverInputRef.current?.click()}
                  >
                    Upload image
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="issue-title">Issue Title *</Label>
            <Input id="issue-title" value={title} onChange={(e) => handleTitleChange(e.target.value)} placeholder="Bitcoin Magazine — May 2024" className="mt-1.5" />
          </div>

          {/* Issue Number */}
          <div>
            <Label htmlFor="issue-num">Issue Number / Identifier *</Label>
            <Input id="issue-num" value={issueId} onChange={(e) => setIssueId(e.target.value)} placeholder="May 2024, Vol. 3, 12..." className="mt-1.5" />
          </div>

          {/* Slug */}
          <div>
            <Label htmlFor="issue-slug">Slug (unique identifier)</Label>
            <Input
              id="issue-slug"
              value={slug}
              onChange={(e) => { setSlug(e.target.value); setSlugManual(true); }}
              placeholder="2024-05"
              className="mt-1.5 font-mono text-sm"
            />
          </div>

          {/* Publication Date */}
          <div>
            <Label htmlFor="issue-date">Original Publication Date</Label>
            <Input id="issue-date" type="date" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} className="mt-1.5 w-48" />
          </div>

          {/* Summary */}
          <div>
            <Label htmlFor="issue-summary">Summary / Editorial Note</Label>
            <Input id="issue-summary" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Short description of this issue" className="mt-1.5" />
          </div>

          {/* Content */}
          <div>
            <Label htmlFor="issue-content">Table of Contents / Notes (Markdown)</Label>
            <Textarea id="issue-content" value={content} onChange={(e) => setContent(e.target.value)} placeholder="Editorial notes or table of contents..." rows={3} className="mt-1.5" />
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handleUpload}
              className="flex-1"
              disabled={!pdfFile || !title.trim() || !issueId.trim()}
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Issue
            </Button>
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
